import React, { useEffect, useState, useCallback } from 'react';
import { BarChart2, RefreshCw, ChevronDown, ChevronRight, X, School, Hash, Tag, Clock, Users, BookOpen, CheckCircle, AlertCircle, Search } from 'lucide-react';
import api from '../../api/client';

// ── helpers ────────────────────────────────────────────────────────────────
const pct = (n, t) => (t === 0 ? 0 : Math.round((Number(n) / Number(t)) * 100));
const fmt = (n) => Number(n).toLocaleString('en-IN');

const TYPE_META = {
  direct_batch:  { label: 'Direct Batch',       color: '#C84B0F', bg: '#FEF3EE' },
  print_code:    { label: 'School Print Order',  color: '#1D4ED8', bg: '#EEF2FF' },
  direct_sale:   { label: 'Direct Sale',         color: '#15803D', bg: '#F0FDF4' },
  legacy:        { label: 'Very Old Records',    color: '#6B7280', bg: '#F9FAFB' },
};

// ── mini progress bar ───────────────────────────────────────────────────────
const Bar = ({ value, total, color }) => {
  const p = pct(value, total);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: '#F1EDE8', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: `${p}%`, height: '100%', background: color, borderRadius: 99, transition: 'width 0.4s' }} />
      </div>
      <span style={{ fontSize: 11, color: '#9CA3AF', width: 28, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{p}%</span>
    </div>
  );
};

// ── type badge ─────────────────────────────────────────────────────────────
const TypeBadge = ({ type }) => {
  const m = TYPE_META[type] || TYPE_META.legacy;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 20, background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>
      {m.label}
    </span>
  );
};

// ── stat chip ──────────────────────────────────────────────────────────────
const StatChip = ({ icon: Icon, label, value, color, bg }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: bg, border: `1px solid ${color}22`, borderRadius: 10, padding: '8px 14px' }}>
    <Icon size={15} color={color} strokeWidth={2} />
    <div>
      <div style={{ fontSize: 11, color: '#9CA3AF' }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  </div>
);

// ── detail modal ────────────────────────────────────────────────────────────
const DetailModal = ({ row, onClose }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const params = new URLSearchParams({ orderType: row.order_type, orderGroupId: row.order_group_id });
    api.get(`/std-codes/detail?${params}`)
      .then(r => setCodes(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [row]);

  const filtered = codes.filter(c =>
    !search || c.code?.toLowerCase().includes(search.toLowerCase())
  );

  const statusOf = (c) => {
    if (c.stdOne && c.stdTwo) return 'both';
    if (c.stdOne) return 'login1';
    return 'none';
  };

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 720, maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.18)' }}>

        {/* Modal header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #F3EDE8', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <Tag size={18} color="#C84B0F" />
              <span style={{ fontSize: 17, fontWeight: 700, color: '#1C0A00' }}>
                {row.order_type === 'legacy'
                  ? `${row.school_name || `School ${row.schoolId}`} / ${row.batch_name || `Batch ${row.batchId}`}`
                  : row.order_type === 'direct_batch' && row.order_ref
                    ? row.order_ref
                    : row.order_group_id}
              </span>
              <TypeBadge type={row.order_type} />
            </div>
            <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#9CA3AF' }}>
              <span><strong style={{ color: '#6B4422' }}>School:</strong> {row.school_name || row.schoolId}</span>
              <span><strong style={{ color: '#6B4422' }}>Batch:</strong> {row.batch_name || row.batchId}</span>
              {row.batch_created_at && <span><strong style={{ color: '#6B4422' }}>Date:</strong> {new Date(row.batch_created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: '#FEF3EE', border: 'none', borderRadius: 8, padding: 8, cursor: 'pointer', color: '#C84B0F', display: 'flex', alignItems: 'center' }}>
            <X size={16} />
          </button>
        </div>

        {/* Summary chips */}
        <div style={{ padding: '14px 24px', borderBottom: '1px solid #F3EDE8', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <StatChip icon={Users}       label="Total codes"    value={fmt(row.total_codes)}   color="#C84B0F" bg="#FEF3EE" />
          <StatChip icon={CheckCircle} label="Both logins"    value={fmt(row.both_logins)}   color="#15803D" bg="#F0FDF4" />
          <StatChip icon={CheckCircle} label="Login 1 only"   value={fmt(row.only_login1)}   color="#1D4ED8" bg="#EEF2FF" />
          <StatChip icon={AlertCircle} label="Not redeemed"   value={fmt(row.not_redeemed)}  color="#B91C1C" bg="#FEF2F2" />
        </div>

        {/* Search */}
        <div style={{ padding: '12px 24px', borderBottom: '1px solid #F3EDE8' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#FFF8F4', border: '1px solid #F0DCC8', borderRadius: 8, padding: '8px 12px' }}>
            <Search size={14} color="#C84B0F" />
            <input
              placeholder="Search codes…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, color: '#1C0A00', flex: 1 }} />
          </div>
        </div>

        {/* Code list */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, gap: 10, color: '#9CA3AF', fontSize: 13 }}>
              <div style={{ width: 18, height: 18, border: '2px solid #F0DCC8', borderTopColor: '#C84B0F', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              Loading codes…
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF', fontSize: 13 }}>No codes found</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#FFF8F4' }}>
                  <th style={{ padding: '10px 24px', textAlign: 'left', fontWeight: 600, color: '#9CA3AF', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Code</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, color: '#9CA3AF', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Login 1 (mobile)</th>
                  <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: 600, color: '#9CA3AF', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Login 2 (mobile)</th>
                  <th style={{ padding: '10px 24px', textAlign: 'center', fontWeight: 600, color: '#9CA3AF', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => {
                  const s = statusOf(c);
                  return (
                    <tr key={c.code} style={{ borderTop: '1px solid #FDF0E8', background: i % 2 === 0 ? '#fff' : '#FFFCFA' }}>
                      <td style={{ padding: '10px 24px' }}>
                        <code style={{ background: '#FEF3EE', color: '#C84B0F', padding: '2px 8px', borderRadius: 5, fontSize: 12, fontWeight: 600 }}>{c.code}</code>
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                        {c.stdOne ? (
                          <div>
                            <div style={{ fontWeight: 700, color: '#15803D', fontSize: 13 }}>{c.stdOne_mobile || c.stdOne}</div>
                            {c.stdOne_name && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1 }}>{c.stdOne_name}</div>}
                            <div style={{ fontSize: 11, color: '#C6A882', marginTop: 2 }}>
                              {c.stdOne_updated_at ? new Date(c.stdOne_updated_at).toLocaleDateString('en-IN') : 'Pre-migration'}
                            </div>
                          </div>
                        ) : <span style={{ color: '#D1D5DB' }}>—</span>}
                      </td>
                      <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                        {c.stdTwo ? (
                          <div>
                            <div style={{ fontWeight: 700, color: '#0F766E', fontSize: 13 }}>{c.stdTwo_mobile || c.stdTwo}</div>
                            {c.stdTwo_name && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 1 }}>{c.stdTwo_name}</div>}
                            <div style={{ fontSize: 11, color: '#C6A882', marginTop: 2 }}>
                              {c.stdTwo_updated_at ? new Date(c.stdTwo_updated_at).toLocaleDateString('en-IN') : 'Pre-migration'}
                            </div>
                          </div>
                        ) : <span style={{ color: '#D1D5DB' }}>—</span>}
                      </td>
                      <td style={{ padding: '10px 24px', textAlign: 'center' }}>
                        {s === 'both'   && <span style={{ fontSize: 11, fontWeight: 600, background: '#F0FDF4', color: '#15803D', padding: '3px 10px', borderRadius: 20 }}>Both logins ✓✓</span>}
                        {s === 'login1' && <span style={{ fontSize: 11, fontWeight: 600, background: '#EEF2FF', color: '#1D4ED8', padding: '3px 10px', borderRadius: 20 }}>Login 1 only</span>}
                        {s === 'none'   && <span style={{ fontSize: 11, fontWeight: 600, background: '#F9FAFB', color: '#6B7280', padding: '3px 10px', borderRadius: 20 }}>Not redeemed</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ padding: '12px 24px', borderTop: '1px solid #F3EDE8', fontSize: 12, color: '#9CA3AF', display: 'flex', justifyContent: 'space-between' }}>
          <span>Showing {filtered.length} of {codes.length} codes</span>
          {codes.some(c => (c.stdOne || c.stdTwo) && !c.stdOne_updated_at) && (
            <span>* Pre-migration: code was redeemed before timestamp tracking was added</span>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ── order card ─────────────────────────────────────────────────────────────
const OrderCard = ({ row, onViewDetail }) => {
  const total = Number(row.total_codes);
  const l1 = Number(row.login1_done);
  const l2 = Number(row.login2_done);
  const nr = Number(row.not_redeemed);

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #F0DCC8',
      borderRadius: 14,
      overflow: 'hidden',
      transition: 'box-shadow 0.15s',
      boxShadow: '0 1px 4px rgba(200,75,15,0.06)',
    }}>
      {/* Card header */}
      <div style={{ padding: '14px 18px', borderBottom: '1px solid #FDF0E8', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, background: '#FFFCFA' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: '#C84B0F', background: '#FEF3EE', padding: '2px 8px', borderRadius: 6, border: '1px solid #FDCFB5' }}>
              {row.order_type === 'legacy'
                ? `${row.school_name || `School ${row.schoolId}`}`
                : row.order_type === 'direct_batch' && row.order_ref
                  ? row.order_ref
                  : row.order_group_id}
            </span>
            {row.order_type === 'direct_batch' && row.order_status && (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.05em',
                background: row.order_status === 'approved' ? '#F0FDF4' : row.order_status === 'draft' ? '#F9FAFB' : '#FEF3EE',
                color:      row.order_status === 'approved' ? '#15803D' : row.order_status === 'draft' ? '#6B7280' : '#C84B0F',
                border:     `1px solid ${row.order_status === 'approved' ? '#BBF7D0' : row.order_status === 'draft' ? '#E5E7EB' : '#FDCFB5'}`,
              }}>
                {row.order_status.replace('_', ' ')}
              </span>
            )}
            <TypeBadge type={row.order_type} />
          </div>
          <div style={{ display: 'flex', gap: 14, fontSize: 12, color: '#9CA3AF', flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <School size={12} /> {row.school_name || `School ID: ${row.schoolId}`}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <BookOpen size={12} /> {row.batch_name || `Batch: ${row.batchId}`}
            </span>
            {row.batch_created_at && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Clock size={12} /> {new Date(row.batch_created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => onViewDetail(row)}
          style={{ flexShrink: 0, background: '#FEF3EE', border: '1px solid #FDCFB5', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, color: '#C84B0F', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          View codes →
        </button>
      </div>

      {/* Stats grid */}
      <div style={{ padding: '14px 18px', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
        {/* Total */}
        <div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 2 }}>Total codes</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#1C0A00', fontVariantNumeric: 'tabular-nums' }}>{fmt(total)}</div>
        </div>
        {/* Login 1 */}
        <div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 2 }}>Login 1 (stdOne)</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#15803D', marginBottom: 4, fontVariantNumeric: 'tabular-nums' }}>{fmt(l1)}<span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 400 }}> / {fmt(total)}</span></div>
          <Bar value={l1} total={total} color="#22C55E" />
        </div>
        {/* Login 2 */}
        <div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 2 }}>Login 2 (stdTwo)</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0F766E', marginBottom: 4, fontVariantNumeric: 'tabular-nums' }}>{fmt(l2)}<span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 400 }}> / {fmt(total)}</span></div>
          <Bar value={l2} total={total} color="#14B8A6" />
        </div>
        {/* Not redeemed */}
        <div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 2 }}>Not redeemed</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#B91C1C', marginBottom: 4, fontVariantNumeric: 'tabular-nums' }}>{fmt(nr)}<span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 400 }}> remaining</span></div>
          <Bar value={nr} total={total} color="#EF4444" />
        </div>
      </div>
    </div>
  );
};

// ── main page ──────────────────────────────────────────────────────────────
export default function RedemptionReportPage() {
  const [data, setData]           = useState([]);
  const [schools, setSchools]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [filterSchool, setFilterSchool] = useState('');
  const [filterType, setFilterType]     = useState('');
  const [detailRow, setDetailRow]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      if (filterSchool) params.set('schoolId', filterSchool);
      if (filterType)   params.set('orderType', filterType);
      const res = await api.get(`/std-codes/report?${params}`);
      setData(res.data.data || []);
      setSchools(res.data.schools || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load report');
    } finally { setLoading(false); }
  }, [filterSchool, filterType]);

  useEffect(() => { load(); }, [load]);

  const totals = data.reduce((acc, r) => ({
    total:  acc.total  + Number(r.total_codes),
    login1: acc.login1 + Number(r.login1_done),
    login2: acc.login2 + Number(r.login2_done),
    none:   acc.none   + Number(r.not_redeemed),
  }), { total: 0, login1: 0, login2: 0, none: 0 });

  // Group cards by order type for visual separation
  const grouped = Object.entries(
    data.reduce((acc, row) => {
      const k = row.order_type;
      if (!acc[k]) acc[k] = [];
      acc[k].push(row);
      return acc;
    }, {})
  );

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1100, margin: '0 auto' }}>

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <BarChart2 size={22} color="#C84B0F" />
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1C0A00', margin: 0 }}>Code Redemption Report</h1>
          </div>
          <p style={{ fontSize: 13, color: '#9CA3AF', margin: 0 }}>
            All orders — grouped by direct batch, print code, or direct sale
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: loading ? '#FEF3EE' : '#C84B0F', color: loading ? '#C84B0F' : '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', transition: 'background 0.15s' }}>
          <RefreshCw size={14} style={{ animation: loading ? 'spin 0.8s linear infinite' : 'none' }} />
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Filters */}
      <div style={{ background: '#fff', border: '1px solid #F0DCC8', borderRadius: 14, padding: '16px 20px', marginBottom: 24, display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            School
          </label>
          <div style={{ position: 'relative' }}>
            <School size={14} color="#C84B0F" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <select
              value={filterSchool}
              onChange={e => setFilterSchool(e.target.value)}
              style={{ width: '100%', padding: '9px 32px 9px 30px', border: '1px solid #F0DCC8', borderRadius: 9, fontSize: 13, color: '#1C0A00', background: '#FFFCFA', appearance: 'none', cursor: 'pointer', outline: 'none' }}>
              <option value="">All schools</option>
              {schools.map(s => <option key={s.schoolId} value={s.schoolId}>{s.school_name || `School ${s.schoolId}`}</option>)}
            </select>
            <ChevronDown size={14} color="#9CA3AF" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 200 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
            Order type
          </label>
          <div style={{ position: 'relative' }}>
            <Tag size={14} color="#C84B0F" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              style={{ width: '100%', padding: '9px 32px 9px 30px', border: '1px solid #F0DCC8', borderRadius: 9, fontSize: 13, color: '#1C0A00', background: '#FFFCFA', appearance: 'none', cursor: 'pointer', outline: 'none' }}>
              <option value="">All types</option>
              <option value="direct_batch">Direct Batch (new)</option>
              <option value="print_code">School Print Order</option>
              <option value="direct_sale">Direct Customer Sale</option>
              <option value="legacy">Very Old Records</option>
            </select>
            <ChevronDown size={14} color="#9CA3AF" style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
          </div>
        </div>

        {(filterSchool || filterType) && (
          <button
            onClick={() => { setFilterSchool(''); setFilterType(''); }}
            style={{ padding: '9px 16px', border: '1px solid #F0DCC8', borderRadius: 9, fontSize: 13, color: '#C84B0F', background: '#FEF3EE', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            <X size={13} /> Clear
          </button>
        )}
      </div>

      {/* Summary metric cards */}
      {!loading && data.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 28 }}>
          {[
            { label: 'Total codes',   value: fmt(totals.total),  sub: '100% of total',               color: '#C84B0F', bg: '#FEF3EE', border: '#FDCFB5' },
            { label: 'Login 1 done',  value: fmt(totals.login1), sub: `${pct(totals.login1, totals.total)}% of total`, color: '#15803D', bg: '#F0FDF4', border: '#BBF7D0' },
            { label: 'Login 2 done',  value: fmt(totals.login2), sub: `${pct(totals.login2, totals.total)}% of total`, color: '#0F766E', bg: '#F0FDFA', border: '#99F6E4' },
            { label: 'Not redeemed',  value: fmt(totals.none),   sub: `${pct(totals.none, totals.total)}% of total`,  color: '#B91C1C', bg: '#FEF2F2', border: '#FECACA' },
          ].map(m => (
            <div key={m.label} style={{ background: m.bg, border: `1px solid ${m.border}`, borderRadius: 12, padding: '16px 18px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: m.color, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8, opacity: 0.8 }}>{m.label}</div>
              <div style={{ fontSize: 26, fontWeight: 800, color: m.color, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{m.value}</div>
              <div style={{ fontSize: 12, color: m.color, marginTop: 4, opacity: 0.7 }}>{m.sub}</div>
            </div>
          ))}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 0', gap: 12, color: '#9CA3AF', fontSize: 14 }}>
          <div style={{ width: 22, height: 22, border: '2.5px solid #F0DCC8', borderTopColor: '#C84B0F', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          Loading report…
        </div>
      ) : error ? (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 12, padding: '16px 20px', color: '#B91C1C', fontSize: 13 }}>
          {error}
        </div>
      ) : data.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#9CA3AF' }}>
          <BarChart2 size={40} color="#F0DCC8" style={{ marginBottom: 14 }} />
          <div style={{ fontSize: 16, fontWeight: 600, color: '#6B4422', marginBottom: 6 }}>No records found</div>
          <div style={{ fontSize: 13 }}>Try clearing your filters or check back later.</div>
        </div>
      ) : (
        <div>
          {grouped.map(([type, rows]) => (
            <div key={type} style={{ marginBottom: 32 }}>
              {/* Section heading */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <TypeBadge type={type} />
                <span style={{ fontSize: 12, color: '#9CA3AF' }}>{rows.length} order group{rows.length !== 1 ? 's' : ''}</span>
                <div style={{ flex: 1, height: 1, background: '#F0DCC8' }} />
              </div>

              {/* Card grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(480px, 1fr))', gap: 14 }}>
                {rows.map(row => (
                  <OrderCard
                    key={`${row.order_type}_${row.order_group_id}`}
                    row={row}
                    onViewDetail={setDetailRow}
                  />
                ))}
              </div>
            </div>
          ))}

          {/* Footer note */}
          <div style={{ background: '#FFF8F4', border: '1px solid #F0DCC8', borderRadius: 10, padding: '12px 16px', fontSize: 12, color: '#9CA3AF', marginTop: 8 }}>
            ℹ️ Login timestamps are recorded only for codes updated after the DB migration.
            Older records where stdOne/stdTwo is already set will show <em>Pre-migration</em> as the date in the detail view.
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detailRow && <DetailModal row={detailRow} onClose={() => setDetailRow(null)} />}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}